import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, Bell, Banknote, Download, ExternalLink, LogOut, Pencil, Plus, Send, Trash2, Undo2 } from "lucide-react";
import { setupAdminPwa, enablePush, pushSupported } from "../lib/pwa";
import { SocialIcon, ICON_OPTS } from "../lib/social";
import { toast } from "sonner";
import { api, adminHeaders, errMsg, BASE, authWithGoogle } from "../lib/api";
import { Logo } from "../components/site/Logo";

const TOKEN = "wouffa_admin_token";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

const Login = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN, data.token); onDone();
    } catch (err) { setError(errMsg(err)); }
    setBusy(false);
  };
  const field = "w-full rounded-xl border border-amber-900/50 bg-[#0a0908] px-4 py-3 text-base text-stone-100 outline-none focus:border-amber-500/70";
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0a0908] px-5">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl border border-amber-800/40 bg-[#12100e] p-8" data-testid="admin-login-form">
        <Logo size={56} />
        <h1 className="mt-6 font-display text-2xl text-amber-50">Espace du Maître</h1>
        <p className="mt-2 text-sm text-stone-400">Connectez-vous pour lire et répondre aux messages.</p>
        <input className={`${field} mt-8`} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" required />
        <input className={`${field} mt-3`} type="password" placeholder="Mot de passe" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" required />
        {error && <p className="mt-3 text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
        <button disabled={busy} className="mt-6 w-full rounded-full bg-amber-400 py-3 font-bold uppercase tracking-wider text-stone-950 disabled:opacity-50" data-testid="admin-login-submit">
          {busy ? "Connexion..." : "Se connecter"}
        </button>
        <div className="my-5 flex items-center gap-3">
          <span className="h-px flex-1 bg-amber-900/40" />
          <span className="text-xs text-stone-400">ou</span>
          <span className="h-px flex-1 bg-amber-900/40" />
        </div>
        <button type="button" onClick={authWithGoogle} data-testid="admin-google-login"
          className="flex w-full items-center justify-center gap-3 rounded-full border border-amber-700/50 bg-white py-3 text-sm font-semibold text-stone-800 transition-colors hover:bg-stone-100">
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.3-2.1 3.7-5.2 3.7-8.6z" />
            <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.8-5.1L1.3 17.2C3.3 21.2 7.3 24 12 24z" />
            <path fill="#FBBC05" d="M5.2 14.3c-.3-.7-.4-1.5-.4-2.3s.2-1.6.4-2.3L1.3 6.8C.5 8.4 0 10.1 0 12s.5 3.6 1.3 5.2l3.9-2.9z" />
            <path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.3 0 3.3 2.8 1.3 6.8l3.9 2.9c.9-3 3.6-5 6.8-5z" />
          </svg>
          Continuer avec Google
        </button>
        <a href="/wouffa-messages.apk" download data-testid="admin-download-apk"
          className="mt-4 block rounded-full border border-amber-700/50 py-3 text-center text-sm font-semibold text-amber-200 transition-colors hover:bg-amber-400 hover:text-stone-950">
          Télécharger l'application Android (.apk)
        </a>
      </form>
    </div>
  );
};

const useInbox = (onAuthFail) => {
  const [convos, setConvos] = useState([]);
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/admin/conversations", { headers: adminHeaders() });
        if (alive) setConvos(Array.isArray(data) ? data : []);
      } catch (e) { if (e?.response?.status === 401) onAuthFail(); }
    };
    tick();
    const t = setInterval(tick, 2000);
    return () => { alive = false; clearInterval(t); };
  }, [onAuthFail]);
  return [convos, setConvos];
};

const Thread = ({ sid, onBack, onDelete }) => {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const box = useRef(null);
  useEffect(() => {
    setMsgs([]);
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/admin/conversations/${sid}/messages`, { headers: adminHeaders() });
        if (alive) setMsgs(Array.isArray(data) ? data : []);
      } catch { /* retry next tick */ }
    };
    tick();
    const t = setInterval(tick, 2000);
    return () => { alive = false; clearInterval(t); };
  }, [sid]);
  useEffect(() => { if (box.current) box.current.scrollTop = box.current.scrollHeight; }, [msgs.length]);
  const send = async (e) => {
    e.preventDefault();
    const t = text.trim(); if (!t) return;
    try {
      const { data } = await api.post(`/admin/conversations/${sid}/messages`, { text: t }, { headers: adminHeaders() });
      setMsgs((m) => [...m, data]); setText("");
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="flex h-full flex-col" data-testid="admin-thread">
      <div className="flex items-center justify-between border-b border-amber-900/40 px-5 py-4">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-stone-300 md:hidden" data-testid="admin-thread-back"><ArrowLeft className="h-4 w-4" /> Retour</button>
        <p className="hidden font-mono-x text-xs uppercase tracking-widest text-amber-400 md:block">Visiteur #{sid.slice(0, 6)}</p>
        <button onClick={() => onDelete(sid)} className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300" data-testid="admin-thread-delete"><Trash2 className="h-4 w-4" /> Supprimer</button>
      </div>
      <div ref={box} className="flex-1 space-y-3 overflow-y-auto p-5">
        {msgs.map((m) => {
          const mine = m.sender !== "visitor";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`admin-msg-${m.sender}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-amber-400 text-stone-950" : "border border-amber-900/50 bg-[#181512] text-stone-100"}`}>
                <p className="whitespace-pre-wrap break-words">{m.text}</p>
                <p className={`mt-1 text-[11px] ${mine ? "text-stone-800" : "text-stone-400"}`}>{m.sender === "auto" ? "Réponse auto · " : ""}{fmt(m.created_at)}</p>
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="flex gap-2 border-t border-amber-900/40 p-3">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Votre réponse..." data-testid="admin-reply-input"
          className="flex-1 rounded-full border border-amber-900/50 bg-[#0a0908] px-4 py-3 text-base text-stone-100 outline-none focus:border-amber-500/70 md:text-sm" />
        <button className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-400 text-stone-950" data-testid="admin-reply-send" aria-label="Envoyer"><Send className="h-4 w-4" /></button>
      </form>
    </div>
  );
};

const AppActions = () => {
  const [prompt, setPrompt] = useState(null);
  const [on, setOn] = useState(() => typeof Notification !== "undefined" && Notification.permission === "granted");
  useEffect(() => {
    const h = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);
  const notify = async () => {
    try { await enablePush(); setOn(true); toast.success("Notifications activées sur cet appareil."); }
    catch (e) { toast.error(e?.message || "Activation impossible."); }
  };
  const install = async () => { await prompt.prompt(); setPrompt(null); };
  const btn = "flex items-center gap-1.5 rounded-full border border-amber-700/50 px-3 py-1.5 text-xs font-semibold text-amber-200 hover:bg-amber-400 hover:text-stone-950";
  return (
    <div className="flex items-center gap-2">
      {prompt && <button onClick={install} className={btn} data-testid="admin-install-app"><Download className="h-3.5 w-3.5" /> Installer l'app</button>}
      {pushSupported() && (
        <button onClick={notify} className={btn} data-testid="admin-enable-push">
          <Bell className="h-3.5 w-3.5" /> {on ? "Tester les notifs" : "Activer les notifs"}
        </button>
      )}
    </div>
  );
};

const chime = () => {
  try { new Audio("/message-chime.wav").play().catch(() => {}); } catch { /* audio indisponible */ }
};

const Payments = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/payments", { headers: adminHeaders() });
        if (alive) setRows(Array.isArray(data) ? data : []);
      } catch { if (alive) setRows([]); }
    };
    load();
    const t = setInterval(load, 5000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  const fmtAmt = (a, c) => `${(a / 100).toFixed(2).replace(".", ",")} ${c === "eur" ? "€" : c.toUpperCase()}`;
  const badge = { paid: "bg-emerald-600", pending: "bg-amber-600", failed: "bg-red-700", expired: "bg-stone-600", refunded: "bg-stone-600" };

  const [amount, setAmount] = useState("");
  const [label, setLabel] = useState("");
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const createLink = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/payments/link",
        { amount_eur: parseFloat(amount.replace(",", ".")), label: label.trim() || "Travail Maître Wouffa", origin_url: window.location.origin },
        { headers: adminHeaders() });
      setLink(data); setAmount(""); setLabel("");
      toast.success("Lien de paiement créé.");
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(link.url); toast.success("Lien copié — collez-le dans le chat."); }
    catch { toast.error("Copie impossible."); }
  };
  const refund = async (sid) => {
    if (!window.confirm("Rembourser ce paiement en totalité ?")) return;
    try {
      await api.post(`/admin/payments/${sid}/refund`, {}, { headers: adminHeaders() });
      toast.success("Paiement remboursé.");
      const { data } = await api.get("/admin/payments", { headers: adminHeaders() });
      setRows(data);
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="w-full overflow-y-auto p-5 md:p-8" data-testid="admin-payments">
      <div className="mx-auto max-w-2xl">
        <h2 className="font-display text-lg text-amber-100 md:text-xl">Paiements reçus</h2>
        <p className="mt-2 text-sm text-stone-400">Règlements encaissés par carte bancaire.</p>

        <form onSubmit={createLink} className="mt-6 space-y-3 rounded-2xl border border-amber-800/40 bg-[#12100e] p-5" data-testid="admin-custom-payment-form">
          <p className="font-mono-x text-[11px] uppercase tracking-[0.25em] text-amber-400">Demander un paiement</p>
          <p className="text-sm text-stone-400">Prix non fixe (matchs, travaux spéciaux) : créez un lien du montant exact, puis collez-le dans le chat du visiteur.</p>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="relative">
              <input className="w-full rounded-xl border border-amber-900/50 bg-[#0a0908] px-3 py-2.5 pr-9 text-sm text-stone-100 outline-none focus:border-amber-500/70"
                type="number" min="5" max="10000" step="0.01" placeholder="Montant" value={amount}
                onChange={(e) => setAmount(e.target.value)} data-testid="admin-custom-amount" required />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-amber-300">€</span>
            </div>
            <input className="w-full rounded-xl border border-amber-900/50 bg-[#0a0908] px-3 py-2.5 text-sm text-stone-100 outline-none focus:border-amber-500/70 md:col-span-2"
              placeholder="Objet (ex : Match PSG - OM)" value={label} maxLength={80}
              onChange={(e) => setLabel(e.target.value)} data-testid="admin-custom-label" />
          </div>
          <button disabled={busy || !amount} className="w-full rounded-full bg-amber-400 py-2.5 text-sm font-bold uppercase tracking-wider text-stone-950 disabled:opacity-50" data-testid="admin-custom-create">
            {busy ? "Création..." : "Créer le lien de paiement"}
          </button>
          {link && (
            <div className="rounded-xl border border-emerald-800/50 bg-emerald-900/20 p-4" data-testid="admin-custom-result">
              <p className="text-sm text-emerald-300">{link.label} — {String(link.amount_eur).replace(".", ",")} €</p>
              <p className="mt-1 break-all text-xs text-stone-300">{link.url}</p>
              <button type="button" onClick={copyLink} className="mt-3 rounded-full bg-amber-400 px-5 py-2 text-sm font-bold text-stone-950" data-testid="admin-custom-copy">
                Copier le lien
              </button>
            </div>
          )}
        </form>

        <div className="mt-8 space-y-2 pb-10">
          <UsdtCard />
          <h3 className="mt-6 font-mono-x text-[11px] uppercase tracking-[0.25em] text-amber-400">Historique</h3>
          {rows === null && <p className="text-sm text-stone-400">Chargement...</p>}
          {rows?.length === 0 && <p className="text-sm text-stone-400">Aucun paiement pour le moment.</p>}
          {rows?.map((p) => (
            <div key={p.session_id} className="flex items-center justify-between gap-3 rounded-xl border border-amber-900/40 bg-[#181512] px-4 py-3" data-testid={`admin-payment-${p.session_id}`}>
              <div className="min-w-0">
                <p className="truncate text-sm text-stone-100">{p.email || "Visiteur"}{p.chat_sid ? ` · Chat #${p.chat_sid.slice(0, 6)}` : ""}</p>
                <p className="truncate text-xs text-stone-400">{p.label ? `${p.label} · ` : ""}{fmt(p.created_at)}</p>
              </div>
        <div className="flex shrink-0 items-center gap-3">
                <span className="font-semibold text-amber-200">{fmtAmt(p.amount || 0, p.currency)}</span>
                <span className={`rounded-full px-2.5 py-1 text-xs font-bold text-white ${badge[p.payment_status] || "bg-stone-600"}`} data-testid={`payment-status-${p.payment_status}`}>
                  {p.payment_status === "paid" ? "Payé" : p.payment_status === "pending" ? "En attente" : p.payment_status === "failed" ? "Refusé" : p.payment_status === "refunded" ? "Remboursé" : "Expiré"}
                </span>
                {p.payment_status === "paid" && (
                  <button onClick={() => refund(p.session_id)} aria-label="Rembourser"
                    className="rounded-full border border-red-800/50 p-2 text-red-400 hover:bg-red-900/30" data-testid={`admin-refund-${p.session_id}`}>
                    <Undo2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CONTENT_FIELDS = [
  ["hero_subtitle", "Sous-titre du haut de page (2 lignes)"],
  ["love_paragraph", "Texte « Retour d'affection »"],
  ["work_paragraph", "Texte « Travail & carrière »"],
  ["voyance_paragraph", "Texte « Voyance »"],
  ["football_paragraph", "Texte « Matchs de football »"],
  ["private_intro", "Texte « Autres travaux en privé »"],
  ["offerings_intro", "Texte « Offrandes »"],
  ["hero_img", "Image du haut de page (lien https...)"],
  ["img_love", "Image section amour (lien)"],
  ["img_candles", "Grande image galerie (lien)"],
  ["img_talismans", "Image talismans (lien)"],
  ["img_cercle", "Image cercle mystique (lien)"],
  ["img_carre", "Image carré mystique (lien)"],
];

const IMG_DEFAULTS = {
  hero_img: "/img/candles2.jpg",
  img_love: "/img/candles4.jpg",
  img_candles: "/img/candles1.jpg",
  img_talismans: "/img/talisman2.jpg",
  img_cercle: "/img/cercle-mystique.jpg",
  img_carre: "/img/carre-mystique.jpg",
};

const ContentEditor = () => {
  const [values, setValues] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/content").then((r) => setValues(r.data || {})).catch(() => setValues({}));
  }, []);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.put("/admin/content", { values }, { headers: adminHeaders() });
      toast.success("Modifications publiées sur le site.");
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const field = "w-full rounded-xl border border-amber-900/50 bg-[#0a0908] px-3 py-2.5 text-sm text-stone-100 outline-none focus:border-amber-500/70";
  return (
    <form onSubmit={save} className="w-full overflow-y-auto p-5 md:p-8" data-testid="admin-content">
      <div className="mx-auto max-w-2xl">
        <h2 className="font-display text-lg text-amber-100 md:text-xl">Modifier le site</h2>
        <p className="mt-2 text-sm text-stone-400">Laissez vide pour garder le texte actuel. Les prix encaissés (150€ / 30€) restent liés à Stripe.</p>
        {values === null && <p className="mt-6 text-sm text-stone-400">Chargement...</p>}
        {values !== null && (
          <div className="mt-6 space-y-4 pb-10">
            {CONTENT_FIELDS.map(([key, label]) => {
              const isImg = key.startsWith("img_") || key === "hero_img";
              return (
                <label key={key} className="block">
                  <span className="mb-1.5 block font-mono-x text-[11px] uppercase tracking-[0.2em] text-amber-400">{label}</span>
                  {isImg ? (
                    <>
                      <img src={values[key] || IMG_DEFAULTS[key]} alt={label}
                        className="mb-2 h-24 w-full rounded-xl border border-amber-900/40 object-cover"
                        data-testid={`content-preview-${key}`} />
                      <input className={field} type="url" placeholder="https://... (laisser vide = image actuelle)"
                        value={values[key] || ""} onChange={(e) => setValues({ ...values, [key]: e.target.value })}
                        data-testid={`content-${key}`} />
                    </>
                  ) : (
                    <textarea className={`${field} min-h-24`} value={values[key] || ""}
                      onChange={(e) => setValues({ ...values, [key]: e.target.value })}
                      data-testid={`content-${key}`} />
                  )}
                </label>
              );
            })}
            <button disabled={busy} className="w-full rounded-full bg-amber-400 py-3 font-bold uppercase tracking-wider text-stone-950 disabled:opacity-50" data-testid="content-save">
              {busy ? "Enregistrement..." : "Publier les modifications"}
            </button>
          </div>
        )}
      </div>
    </form>
  );
};

const Media = () => {
  const [rows, setRows] = useState(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/media", { headers: adminHeaders() });
      setRows(Array.isArray(data) ? data : []);
    } catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Choisissez une photo ou une vidéo."); return; }
    if (f.size > 50 * 1024 * 1024) { toast.error("Fichier trop grand (maximum 50 Mo)."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("title", title.trim());
      await api.post("/admin/media/upload", fd, { headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" } });
      toast.success("Publié sur le site.");
      setTitle(""); if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const toggle = async (id) => { try { await api.post(`/admin/media/${id}/toggle`, {}, { headers: adminHeaders() }); load(); } catch (err) { toast.error(errMsg(err)); } };
  const remove = async (id) => {
    if (!window.confirm("Retirer ce média du site ?")) return;
    try { await api.delete(`/admin/media/${id}`, { headers: adminHeaders() }); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const field = "w-full rounded-xl border border-amber-900/50 bg-[#0a0908] px-3 py-2.5 text-sm text-stone-100 outline-none focus:border-amber-500/70";
  return (
    <div className="w-full min-w-0 overflow-y-auto p-5 md:p-8" data-testid="admin-media">
      <div className="mx-auto max-w-2xl">
        <h2 className="font-display text-lg text-amber-100 md:text-xl">Photos &amp; vidéos du site</h2>
        <p className="mt-2 text-sm text-stone-400">Envoyez une photo ou une vidéo (50 Mo max) avec un titre : elle apparaît dans la galerie du site.</p>
        <div className="mt-6 space-y-3 rounded-2xl border border-amber-800/40 bg-[#12100e] p-5" data-testid="admin-media-upload">
          <input ref={fileRef} type="file" accept="image/*,video/*" data-testid="media-file-input"
            className="w-full rounded-xl border border-amber-900/50 bg-[#0a0908] px-3 py-2.5 text-sm text-stone-300 file:mr-3 file:rounded-full file:border-0 file:bg-amber-400 file:px-3 file:py-1.5 file:text-xs file:font-bold file:text-stone-950" />
          <input className={field} placeholder="Titre (ex : Rituel du jeudi soir)" value={title} maxLength={80}
            onChange={(e) => setTitle(e.target.value)} data-testid="media-title-input" />
          <button onClick={upload} disabled={busy} className="w-full rounded-full bg-amber-400 py-2.5 text-sm font-bold uppercase tracking-wider text-stone-950 disabled:opacity-50" data-testid="media-upload-button">
            {busy ? "Envoi..." : "Publier sur le site"}
          </button>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3 pb-10">
          {rows === null && <p className="col-span-2 text-sm text-stone-400">Chargement...</p>}
          {rows?.length === 0 && <p className="col-span-2 text-sm text-stone-400">Aucun média pour le moment.</p>}
          {rows?.map((m) => (
            <div key={m.id} className="overflow-hidden rounded-2xl border border-amber-900/40 bg-[#181512]" data-testid={`admin-media-${m.id}`}>
              {m.type === "photo"
                ? <img src={`${BASE}/api/files/${m.storage_path}`} alt={m.title} className="h-28 w-full object-cover" />
                : <video src={`${BASE}/api/files/${m.storage_path}`} muted playsInline className="h-28 w-full bg-black object-cover" />}
              <div className="p-3">
                <p className="truncate text-sm text-stone-100">{m.title} {!m.enabled && <span className="text-xs text-stone-400">(masqué)</span>}</p>
                <div className="mt-2 flex gap-2">
                  <button onClick={() => toggle(m.id)} className="flex-1 rounded-full border border-amber-700/50 px-2 py-1.5 text-xs text-amber-200" data-testid={`media-toggle-${m.id}`}>{m.enabled ? "Masquer" : "Afficher"}</button>
                  <button onClick={() => remove(m.id)} aria-label="Supprimer" className="rounded-full border border-red-800/50 p-1.5 text-red-400" data-testid={`media-delete-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const UsdtCard = () => {
  const [addr, setAddr] = useState("");
  const [network, setNetwork] = useState("TRC-20");
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/content").then((r) => {
      const d = r.data || {};
      setAddr(d.usdt_address || ""); setNetwork(d.usdt_network || "TRC-20"); setCurrent(d.usdt_address || "");
    }).catch(() => {});
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/content", { values: { usdt_address: addr.trim(), usdt_network: network } }, { headers: adminHeaders() });
      toast.success(addr.trim() ? "Paiement USDT activé sur le site." : "Paiement USDT désactivé.");
      setCurrent(addr.trim());
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const copyInstr = async () => {
    if (!current) { toast.error("Renseignez d'abord votre adresse USDT."); return; }
    const msg = `Paiement en USDT (réseau ${network}) — envoyez le montant convenu au Maître à cette adresse : ${current}. Collez ensuite la preuve d'envoi dans ce chat. Merci.`;
    try { await navigator.clipboard.writeText(msg); toast.success("Instructions copiées — collez-les dans le chat."); }
    catch { toast.error("Copie impossible."); }
  };
  const field = "w-full rounded-xl border border-amber-900/50 bg-[#0a0908] px-3 py-2.5 text-sm text-stone-100 outline-none focus:border-amber-500/70";
  return (
    <div className="rounded-2xl border border-emerald-800/50 bg-emerald-900/10 p-5" data-testid="admin-usdt-card">
      <p className="font-mono-x text-[11px] uppercase tracking-[0.25em] text-emerald-400">Paiement USDT</p>
      <p className="mt-2 text-sm text-stone-400">Collez votre adresse de portefeuille : le site affiche le bloc USDT avec QR.</p>
      <input className={`${field} mt-3`} placeholder="Adresse USDT (ex : TXxxxxxxxxxxxxxxxxxx)" value={addr} maxLength={80}
        onChange={(e) => setAddr(e.target.value)} data-testid="usdt-address-input" />
      <select className={`${field} mt-3`} value={network} onChange={(e) => setNetwork(e.target.value)} data-testid="usdt-network-select">
        {["TRC-20", "ERC-20", "BEP-20"].map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
      <div className="mt-3 flex gap-2">
        <button onClick={save} disabled={busy} className="flex-1 rounded-full bg-amber-400 py-2.5 text-sm font-bold uppercase tracking-wider text-stone-950 disabled:opacity-50" data-testid="usdt-save">{busy ? "..." : "Enregistrer"}</button>
        <button onClick={copyInstr} className="flex-1 rounded-full border border-amber-700/50 py-2.5 text-sm font-semibold text-amber-200" data-testid="usdt-copy-instructions">Copier les instructions</button>
      </div>
      {current && <p className="mt-2 break-all text-[11px] text-stone-400">Actuel : {current}</p>}
    </div>
  );
};

const Reviews = () => {
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/reviews", { headers: adminHeaders() });
      setRows(Array.isArray(data) ? data : []);
    } catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const approve = async (rid) => {
    try { await api.post(`/admin/reviews/${rid}/approve`, {}, { headers: adminHeaders() }); toast.success("Témoignage publié."); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (rid) => {
    if (!window.confirm("Supprimer ce témoignage ?")) return;
    try { await api.delete(`/admin/reviews/${rid}`, { headers: adminHeaders() }); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const pending = rows?.filter((r) => !r.approved) || [];
  const askMsg = `Bonjour 😊 Si vous êtes satisfait du travail, laissez votre témoignage sur ${window.location.origin}/#temoignages : votre prénom, votre pays et quelques mots. Cela aide beaucoup d'autres personnes. Merci infiniment.`;
  const copyAsk = async () => {
    try { await navigator.clipboard.writeText(askMsg); toast.success("Message copié — collez-le dans le chat du client."); }
    catch { toast.error("Copie impossible."); }
  };
  return (
    <div className="w-full overflow-y-auto p-5 md:p-8" data-testid="admin-reviews">
      <div className="mx-auto max-w-2xl">
        <h2 className="font-display text-lg text-amber-100 md:text-xl">Témoignages clients</h2>
        <p className="mt-2 text-sm text-stone-400">Publiez les témoignages que vous jugez sincères — ils apparaissent sur le site.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={copyAsk} className="rounded-full border border-amber-700/50 px-4 py-2 text-xs font-semibold text-amber-200" data-testid="admin-review-ask-copy">
            Copier le message « demande de témoignage »
          </button>
        </div>
        <div className="mt-6 space-y-2 pb-10">
          {rows === null && <p className="text-sm text-stone-400">Chargement...</p>}
          {rows?.length === 0 && <p className="text-sm text-stone-400">Aucun témoignage pour le moment.</p>}
          {pending.length > 0 && <p className="font-mono-x text-xs uppercase tracking-widest text-amber-400" data-testid="admin-reviews-pending">À valider ({pending.length})</p>}
          {rows?.map((r) => (
            <div key={r.id} className="rounded-xl border border-amber-900/40 bg-[#181512] px-4 py-3" data-testid={`admin-review-${r.id}`}>
              <div className="flex items-center justify-between gap-3">
                <p className="truncate text-sm font-semibold text-amber-100">{r.name}{r.country ? <span className="font-normal text-stone-400"> · {r.country}</span> : null} {!r.approved && <span className="ml-1 rounded-full bg-amber-600 px-2 py-0.5 text-[11px] text-white">en attente</span>}</p>
                <div className="flex shrink-0 gap-2">
                  {!r.approved && (
                    <button onClick={() => approve(r.id)} className="rounded-full border border-emerald-700/60 px-3 py-1.5 text-xs font-semibold text-emerald-300" data-testid={`admin-review-approve-${r.id}`}>Publier</button>
                  )}
                  <button onClick={() => remove(r.id)} aria-label="Supprimer" className="rounded-full border border-red-800/50 p-2 text-red-400" data-testid={`admin-review-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <p className="mt-1 text-sm text-stone-300">{r.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const SocialManager = () => {
  const [links, setLinks] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/social-links", { headers: adminHeaders() });
      setLinks(Array.isArray(data) ? data : []);
    } catch { setLinks([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async (e) => {
    e.preventDefault();
    try {
      const cfg = { headers: adminHeaders() };
      if (edit.id) await api.put(`/admin/social-links/${edit.id}`, edit, cfg);
      else await api.post("/admin/social-links", edit, cfg);
      toast.success("Réseau enregistré."); setEdit(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce réseau ?")) return;
    try { await api.delete(`/admin/social-links/${id}`, { headers: adminHeaders() }); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const field = "w-full rounded-xl border border-amber-900/50 bg-[#0a0908] px-3 py-2.5 text-sm text-stone-100 outline-none focus:border-amber-500/70";
  return (
    <div className="w-full overflow-y-auto p-5 md:p-8" data-testid="admin-social-manager">
      <div className="mx-auto max-w-2xl">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display text-lg text-amber-100 md:text-xl">Réseaux sociaux du site</h2>
          <button onClick={() => setEdit({ name: "", url: "", dm_url: "", icon: "globe", enabled: true, sort: links?.length || 0 })}
            className="flex shrink-0 items-center gap-1.5 rounded-full bg-amber-400 px-4 py-2 text-sm font-bold text-stone-950" data-testid="admin-social-add">
            <Plus className="h-4 w-4" /> Ajouter
          </button>
        </div>
        <p className="mt-2 text-sm text-stone-400">Ces boutons s'affichent dans la section « Partager » du site. Ajoutez ou modifiez n'importe quel réseau quand vous voulez.</p>
        {edit && (
          <form onSubmit={save} className="mt-6 space-y-3 rounded-2xl border border-amber-800/40 bg-[#12100e] p-5" data-testid="admin-social-form">
            <div className="grid gap-3 md:grid-cols-2">
              <input className={field} placeholder="Nom (ex : TikTok)" value={edit.name} maxLength={40}
                onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="admin-social-name" required />
              <select className={field} value={edit.icon} onChange={(e) => setEdit({ ...edit, icon: e.target.value })} data-testid="admin-social-icon">
                {ICON_OPTS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <input className={field} type="url" placeholder="Lien de la page (https://...)" value={edit.url}
              onChange={(e) => setEdit({ ...edit, url: e.target.value })} data-testid="admin-social-url" required />
            <input className={field} type="url" placeholder="Lien message privé (optionnel)" value={edit.dm_url || ""}
              onChange={(e) => setEdit({ ...edit, dm_url: e.target.value || null })} data-testid="admin-social-dm" />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm text-stone-300">
                <input type="checkbox" checked={edit.enabled} onChange={(e) => setEdit({ ...edit, enabled: e.target.checked })}
                  className="h-4 w-4 accent-amber-400" data-testid="admin-social-enabled" /> Afficher sur le site
              </label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setEdit(null)} className="rounded-full border border-amber-700/50 px-4 py-2 text-sm text-stone-300" data-testid="admin-social-cancel">Annuler</button>
                <button className="rounded-full bg-amber-400 px-5 py-2 text-sm font-bold text-stone-950" data-testid="admin-social-save">Enregistrer</button>
              </div>
            </div>
          </form>
        )}
        <div className="mt-6 space-y-2 pb-10">
          {links === null && <p className="text-sm text-stone-400">Chargement...</p>}
          {links?.length === 0 && <p className="text-sm text-stone-400">Aucun réseau pour le moment. Touchez « Ajouter ».</p>}
          {links?.map((l) => (
            <div key={l.id} className="flex items-center justify-between gap-3 rounded-xl border border-amber-900/40 bg-[#181512] px-4 py-3" data-testid={`admin-social-row-${l.id}`}>
              <div className="flex min-w-0 items-center gap-3">
                <SocialIcon id={l.icon} className="h-4 w-4 shrink-0 text-amber-400" />
                <div className="min-w-0">
                  <p className="truncate text-sm text-stone-100">{l.name} {!l.enabled && <span className="text-xs text-stone-400">(masqué)</span>}</p>
                  <p className="truncate text-xs text-stone-400">{l.url}</p>
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                <button onClick={() => setEdit({ ...l })} aria-label="Modifier" className="rounded-full border border-amber-700/50 p-2 text-amber-200" data-testid={`admin-social-edit-${l.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={() => remove(l.id)} aria-label="Supprimer" className="rounded-full border border-red-800/50 p-2 text-red-400" data-testid={`admin-social-delete-${l.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Inbox = ({ onLogout }) => {
  const [convos, setConvos] = useInbox(onLogout);
  const [active, setActive] = useState(() => new URLSearchParams(window.location.search).get("c"));
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return ["media", "payments", "social", "reviews", "content", "messages"].includes(t) ? t : "messages";
  });
  const prevUnread = useRef(null);
  useEffect(() => {
    const total = convos.reduce((n, c) => n + (c.unread || 0), 0);
    if (prevUnread.current !== null && total > prevUnread.current) {
      chime();
      navigator.vibrate?.([200, 100, 200]);
      toast("Nouveau message reçu");
    }
    prevUnread.current = total;
  }, [convos]);
  const del = async (sid) => {
    if (!window.confirm("Supprimer cette conversation ?")) return;
    await api.delete(`/admin/conversations/${sid}`, { headers: adminHeaders() });
    setConvos((c) => c.filter((x) => x.id !== sid)); setActive(null);
  };
  const unread = convos.reduce((n, c) => n + (c.unread || 0), 0);
  return (
    <div className="flex h-[100svh] flex-col overflow-x-hidden bg-[#0a0908] text-stone-100" data-testid="admin-inbox">
      <header className="flex items-center justify-between border-b border-amber-900/40 px-5 py-3">
        <div className="flex items-center gap-3"><Logo size={34} /><span className="font-display text-amber-100">Messages</span>
          {unread > 0 && <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs font-bold" data-testid="admin-unread-total">{unread}</span>}
        </div>
        <div className="flex items-center gap-3">
          <a href="/" aria-label="Voir le site"
            className="flex items-center gap-1.5 rounded-full border border-amber-700/50 px-3 py-1.5 text-xs font-semibold text-amber-200 hover:bg-amber-400 hover:text-stone-950" data-testid="admin-view-site">
            <ExternalLink className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Voir le site</span>
          </a>
          <AppActions />
          <button onClick={onLogout} aria-label="Déconnexion" className="flex items-center gap-2 text-sm text-stone-300 hover:text-amber-300" data-testid="admin-logout"><LogOut className="h-4 w-4" /><span className="hidden sm:inline">Déconnexion</span></button>
        </div>
      </header>
      <div className="flex overflow-x-auto border-b border-amber-900/40" data-testid="admin-tabs">
        <button onClick={() => setTab("messages")} data-testid="admin-tab-messages"
          className={`flex-none whitespace-nowrap px-4 py-3 text-sm font-semibold transition-colors md:px-8 ${tab === "messages" ? "border-b-2 border-amber-400 text-amber-200" : "text-stone-400"}`}>
          Messages
        </button>
        <button onClick={() => setTab("social")} data-testid="admin-tab-social"
          className={`flex-none whitespace-nowrap px-4 py-3 text-sm font-semibold transition-colors md:px-8 ${tab === "social" ? "border-b-2 border-amber-400 text-amber-200" : "text-stone-400"}`}>
          Réseaux sociaux
        </button>
        <button onClick={() => setTab("payments")} data-testid="admin-tab-payments"
          className={`flex-none whitespace-nowrap px-4 py-3 text-sm font-semibold transition-colors md:px-8 ${tab === "payments" ? "border-b-2 border-amber-400 text-amber-200" : "text-stone-400"}`}>
          <Banknote className="mr-1.5 inline h-4 w-4" />Paiements
        </button>
        <button onClick={() => setTab("reviews")} data-testid="admin-tab-reviews"
          className={`flex-none whitespace-nowrap px-4 py-3 text-sm font-semibold transition-colors md:px-8 ${tab === "reviews" ? "border-b-2 border-amber-400 text-amber-200" : "text-stone-400"}`}>
          Avis
        </button>
        <button onClick={() => setTab("media")} data-testid="admin-tab-media"
          className={`flex-none whitespace-nowrap px-4 py-3 text-sm font-semibold transition-colors md:px-8 ${tab === "media" ? "border-b-2 border-amber-400 text-amber-200" : "text-stone-400"}`}>
          Médias
        </button>
        <button onClick={() => setTab("content")} data-testid="admin-tab-content"
          className={`flex-none whitespace-nowrap px-4 py-3 text-sm font-semibold transition-colors md:px-8 ${tab === "content" ? "border-b-2 border-amber-400 text-amber-200" : "text-stone-400"}`}>
          Modifier le site
        </button>
      </div>
      <div className="flex min-h-0 flex-1">
        {tab === "social" && <SocialManager />}
        {tab === "payments" && <Payments />}
        {tab === "reviews" && <Reviews />}
        {tab === "media" && <Media />}
        {tab === "content" && <ContentEditor />}
        {tab === "messages" && (<>
        <aside className={`${active ? "hidden md:block" : "block"} w-full overflow-y-auto border-r border-amber-900/40 md:w-80`} data-testid="admin-conversation-list">
          {convos.length === 0 && <p className="p-6 text-sm text-stone-400">Aucun message pour le moment.</p>}
          {convos.map((c) => (
            <button key={c.id} onClick={() => setActive(c.id)} data-testid={`admin-convo-${c.id}`}
              className={`block w-full border-b border-amber-900/20 px-5 py-4 text-left transition-colors hover:bg-[#181512] ${active === c.id ? "bg-[#181512]" : ""}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono-x text-xs text-amber-400">Visiteur #{c.id.slice(0, 6)}</span>
                <span className="text-[11px] text-stone-400">{fmt(c.last_at)}</span>
              </div>
              <div className="mt-1 flex items-center justify-between gap-2">
                <p className="truncate text-sm text-stone-200">{c.last_text}</p>
                {c.unread > 0 && <span className="rounded-full bg-red-600 px-2 text-xs font-bold">{c.unread}</span>}
              </div>
            </button>
          ))}
        </aside>
        <main className={`${active ? "block" : "hidden md:flex"} min-w-0 flex-1 md:items-center md:justify-center`}>
          {active ? <Thread sid={active} onBack={() => setActive(null)} onDelete={del} />
            : <p className="text-sm text-stone-400">Sélectionnez une conversation.</p>}
        </main>
        </>)}
      </div>
    </div>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(() => !!localStorage.getItem(TOKEN));
  const logout = useCallback(() => {
    api.post("/auth/logout", {}, { withCredentials: true }).catch(() => {});
    localStorage.removeItem(TOKEN);
    setAuthed(false);
  }, []);
  useEffect(() => {
    if (authed) return;
    // connexion automatique si une session Google valide existe déjà
    api.get("/auth/me", { withCredentials: true })
      .then(({ data }) => {
        if (data?.role === "admin" && data?.token) { localStorage.setItem(TOKEN, data.token); setAuthed(true); }
      })
      .catch(() => {});
  }, [authed]);
  useEffect(() => { setupAdminPwa(); }, []);
  return authed ? <Inbox onLogout={logout} /> : <Login onDone={() => setAuthed(true)} />;
}
